import { Group, Button, Text, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useContext, useEffect } from "react";
import Select from "src/components/Select";
import {
  CreateAgreementInput,
  GetAgreementQuery,
  Unit,
  useCreateAgreementMutation,
  useGetPoliciesQuery,
  useUpdateAgreementMutation,
  GroupPricingType,
  PaymentFrequency,
  DraftMode,
  useGetLocationsLazyQuery,
} from "src/generated/graphql";
import { array, boolean, object, string, number } from "yup";
import { useModals } from "@mantine/modals";
import ActionCard from "src/components/ActionCard";
import { useNavigate, useParams } from "react-router-dom";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import MultiSelect from "src/components/MultiSelect";
import Switch from "src/components/Switch/Switch";
import DatePicker from "src/components/DatePicker";
import NumberInput from "src/components/NumberInput/NumberInput";
import { memo } from "react";
import FormCard from "src/components/FormCard";
import {
  CardRadius,
  HeightCard,
  MESSAGES,
  ROUTES,
  daysArray,
  monthArray,
  weeksArray,
} from "src/utils/constants";
import { AppContext } from "src/api/AppContext";
import { GetAgreement } from "src/api/graphql/queries/Agreement";

const agreementInitialValues = {
  name: "",
  description: "",
  isMembership: false,
  isGeneric: false,
  additionalPolicies: [],
  isAutoRenew: false,
  termValue: 0,
  Unit: Unit.Year,
  isPreSale: false,
  isGroup: false,
  groupPricingType: GroupPricingType.Limit,
  limit: 0,
  scalingPrice: 0,
  paymentFrequency: PaymentFrequency.Weekly,
  draftMode: DraftMode.Immediate,
  locations: undefined,
};

const agreementFormSchema = object().shape({
  Unit: string().required("Term Unit is required"),
  termValue: string().required("Term Value is required"),
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should must be least 2 letters")
    .max(30, "Name must not be greater than 30 letters")
    .required("Name is required"),
  description: string().max(200).notRequired(),
  additionalPolicies: array().min(1, "at least 1 Policy is required"),
  isPreSale: boolean(),
  postponeUntil: string().when("isPreSale", {
    is: (isPreSale: boolean) => !!isPreSale,
    then: () => string().required("Date is required"),
  }),
  groupPricingType: string().when("isGroup", {
    is: (isGroup: boolean) => !!isGroup,
    then: () => string().required("Group Pricing Type is required"),
    otherwise: () => string().nullable(),
  }),
  limit: number().when("groupPricingType", {
    is: (groupPricingType: string) =>
      groupPricingType === GroupPricingType.Limit,
    then: () => number().required("Limit is required"),
    otherwise: () => string().nullable().notRequired(),
  }),
  scalingPrice: number().when("groupPricingType", {
    is: (groupPricingType: string) =>
      groupPricingType === GroupPricingType.Scaling,
    then: () => number().required("Price is required"),
    otherwise: () => string().nullable().notRequired(),
  }),
  paymentFrequency: string().required("Payment Frequency is required"),
  draftMode: string().required("Mode is required"),
  draftValue: string().when("draftMode", {
    is: (draftMode: DraftMode) => draftMode !== DraftMode.Immediate,
    then: () => string().required("Draft Value is required"),
    otherwise: () => string().nullable(),
  }),
});

type IFormProps = Omit<
  CreateAgreementInput,
  "additionalPolicies" | "draftValue" | "locations"
> & {
  additionalPolicies: string[];
  locations?: string[];
  isPreSale?: boolean;
  isGroup?: boolean;
  groupPricingType?: string;
  limit?: number;
  scalingPrice?: number;
  draftValue?: string | number;
};

interface IProps {
  isOrg?: boolean;
  agreementData?: GetAgreementQuery["agreementOne"];
  onCreate?: () => void;
  isAllowWrite: boolean;
}

function AgreementForm({
  agreementData,
  onCreate,
  isOrg,
  isAllowWrite,
}: IProps) {
  const navigate = useNavigate();
  const modals = useModals();
  const theme = useMantineTheme();
  const { currentAdmin, currentLocation } = useContext(AppContext);
  const { agreementId } = useParams<{ agreementId: string }>();
  const { data: policiesArr } = useGetPoliciesQuery();
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(
    !!agreementId
  );

  useEffect(() => {
    agreementData && resetFormState();
  }, [agreementData]);

  const [getLocations, { data: locArr }] = useGetLocationsLazyQuery();
  useEffect(() => {
    if (isOrg) {
      getLocations();
    }
  }, [isOrg]);

  const resetFormState = () => {
    if (!agreementData) return;
    const {
      additionalPolicies,
      postponeUntil,
      recurringCharges,
      recurringAllotments,
      initialAllotments,
      initialCharges,
      draftValue,
      draftMode,
      limit,
      scalingPrice,
      locations,
      accessPeriods,
      ...rest
    } = agreementData;
    let _obj = {
      ...rest,
      additionalPolicies: additionalPolicies.map((e) => e.id),
      locations: locations?.map((e) => e.id),
      isPreSale: !!postponeUntil,
      ...(postponeUntil && {
        postponeUntil: new Date(postponeUntil),
      }),
      draftMode,
      ...(draftValue && {
        draftValue:
          draftMode === DraftMode.Dynamic ? String(draftValue) : draftValue,
      }),
      limit: limit || 0,
      scalingPrice: scalingPrice || 0,
    };
    form.setValues(_obj);
    form.resetDirty(_obj);
  };

  const [updateAgreement, { loading: updateLoading }] =
    useUpdateAgreementMutation({
      onCompleted: () => {
        toggleFormActive();
        form.resetDirty();
      },
      refetchQueries: [
        {
          query: GetAgreement,
          variables: { id: agreementId },
        },
      ],
    });

  const [createAgreement, { loading: createLoading }] =
    useCreateAgreementMutation({
      onCompleted: (resp) => {
        if (resp.createAgreement) {
          modals.closeAll();
          navigate(
            `${isOrg ? ROUTES.ORG_AGREEMENT : ROUTES.AGREEMENT}/${
              resp.createAgreement.id
            }`
          );
        } else {
          onCreate ? onCreate() : window.location.reload();
        }
      },
    });

  const form = useForm<IFormProps>({
    initialValues: {
      ...agreementInitialValues,
      isGeneric: Boolean(isOrg),
      isOrg: isOrg || false,
    },
    validate: yupResolver(agreementFormSchema),
  });
  const handleSubmit = async (values: IFormProps) => {
    const {
      isPreSale,
      postponeUntil,
      limit,
      scalingPrice,
      groupPricingType,
      ...rest
    } = values;
    const isLimit = groupPricingType === GroupPricingType.Limit;
    const isScaling = groupPricingType === GroupPricingType.Scaling;
    let _obj = {
      ...rest,
      additionalPolicies: values.additionalPolicies?.map((e) => ({ id: e })),
      postponeUntil: isPreSale ? postponeUntil : null,
      groupPricingType: values.isGroup ? groupPricingType : null,
      limit: values.isGroup && isLimit ? limit : null,
      scalingPrice: values.isGroup && isScaling ? scalingPrice : null,
      draftValue:
        rest.draftMode !== DraftMode.Immediate ? Number(rest.draftValue) : null,
    };

    const handleUpdateAgreement = (isForceUpdate?: boolean) => {
      updateAgreement({
        variables: {
          data: isOrg
            ? {
                ..._obj,
                locations: (values.locations || []).map((e) => ({ id: e })),
              }
            : {
                ..._obj,
                locations: currentLocation ? [{ id: currentLocation.id }] : [],
              },
          id: agreementId!,
          ...(isForceUpdate && { isForceUpdate: true }),
        },
      });
    };

    const openModal = () =>
      modals.openConfirmModal({
        closeOnClickOutside: false,
        title: "Agreement Force Update",
        children: (
          <Text size="sm">
            Would you like to force these changes to apply to signed agreements
            that are currently active?
          </Text>
        ),
        labels: { confirm: "Confirm", cancel: "Cancel" },
        onCancel: () => handleUpdateAgreement(),
        onConfirm: () => handleUpdateAgreement(true),
      });

    if (agreementId && currentAdmin) {
      openModal();
    } else {
      agreementId
        ? handleUpdateAgreement()
        : createAgreement({
            variables: {
              data: isOrg
                ? {
                    ..._obj,
                    locations: (values.locations || []).map((e) => ({ id: e })),
                  }
                : {
                    ..._obj,
                    isGeneric: false,
                    locations: currentLocation
                      ? [{ id: currentLocation.id }]
                      : [],
                  },
            },
          });
    }
  };

  const handleEdit = () => {
    if (!isOrg) {
      if (form.values.isOrg) {
        modals.openModal({
          closeOnClickOutside: true,
          title: "Error!",
          children: (
            <Text size="sm">
              This is an org-level agreement. You do not have permission to edit
              this agreement.
            </Text>
          ),
        });
        return;
      }
    }
    toggleFormActive();
  };

  const handleClick = () => {
    resetFormState();
    toggleFormActive();
  };

  const isFormUpdated = form.isDirty() && form.isTouched();
  let currentFrequency = form.getInputProps("paymentFrequency").value;

  const handleFrequencyChange = (e: PaymentFrequency) => {
    form.setFieldValue("paymentFrequency", e);
    form.getInputProps("draftMode").value === DraftMode.Dynamic &&
      form.setFieldValue("draftValue", "");
  };

  const handleModeChange = (e: DraftMode) => {
    form.setFieldValue("draftMode", e);
    form.setFieldValue(
      "draftValue",
      form.getInputProps("draftMode").value === DraftMode.Dynamic
        ? ""
        : undefined
    );
  };

  return (
    <FormCard
      title={"Basic Info"}
      loading={updateLoading}
      bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
      onCancel={handleClick}
      onEdit={isAllowWrite ? handleEdit : undefined}
      onSave={form.onSubmit((values) => handleSubmit(values))}
      isFormActive={isFormActive}
      hideCard={!agreementId}
    >
      <ActionCard
        title={"General"}
        height={HeightCard.sm}
        radius={CardRadius.sm}
        size="md"
        headerOnly
      >
        <TextInput
          mt={"xs"}
          withAsterisk
          label="Name"
          disabled={isFormActive}
          {...form.getInputProps("name")}
        />

        <Textarea
          label="Description"
          disabled={isFormActive}
          {...form.getInputProps("description")}
        />

        <MultiSelect
          //withAsterisk
          data={
            policiesArr?.policyMany?.map((val) => ({
              value: val.id,
              label: val.name,
            })) || []
          }
          disabled={isFormActive}
          label="Additional Policies"
          {...form.getInputProps("additionalPolicies")}
        />

        <Switch
          labelPosition="left"
          label="Membership"
          checked={form.getInputProps("isMembership").value}
          {...form.getInputProps("isMembership")}
          disabled={isFormActive}
          mt="md"
        />
        {form.getInputProps("isMembership").value && (
          <Text size={13} ml={7} color={theme.colors.secondaryTextColor[6]}>
            When selected, this allows general access to the facility
          </Text>
        )}
      </ActionCard>

      <ActionCard
        title={"Term"}
        mt={"sm"}
        height={HeightCard.sm}
        radius={CardRadius.sm}
        size="md"
        headerOnly
      >
        <NumberInput
          mt={"xs"}
          withAsterisk
          label="Term Length"
          {...form.getInputProps("termValue")}
          disabled={isFormActive}
        />

        <Select
          withAsterisk
          data={
            Object.keys(Unit)?.map((val) => ({
              value: val,
              label: val,
            })) || []
          }
          disabled={isFormActive}
          label="Term Unit"
          {...form.getInputProps("Unit")}
        />

        <Switch
          labelPosition="left"
          label="Auto-Renew"
          checked={form.getInputProps("isAutoRenew").value}
          {...form.getInputProps("isAutoRenew")}
          disabled={isFormActive}
          mt="sm"
        />

        {form.getInputProps("isAutoRenew").value && (
          <Text size={13} ml={7} color={theme.colors.secondaryTextColor[6]}>
            Auto renew details and description
          </Text>
        )}
      </ActionCard>

      <ActionCard
        title={"Recurring Payment Setup"}
        mt={"sm"}
        height={HeightCard.sm}
        radius={CardRadius.sm}
        size="md"
        headerOnly
      >
        <Select
          mt={"xs"}
          withAsterisk
          data={
            Object.keys(PaymentFrequency)?.map((val) => ({
              value: val,
              label: val,
            })) || []
          }
          label="Frequency"
          {...form.getInputProps("paymentFrequency")}
          onChange={handleFrequencyChange}
          disabled={isFormActive}
        />

        <Text size={16} mt={10} pl={2}>
          Draft
        </Text>

        <Select
          withAsterisk
          data={
            Object.keys(DraftMode)?.map((val) => ({
              value: val,
              label: val,
            })) || []
          }
          label="Mode"
          {...form.getInputProps("draftMode")}
          onChange={handleModeChange}
          disabled={isFormActive}
        />

        {form.getInputProps("draftMode").value === DraftMode.Buffer && (
          <NumberInput
            withAsterisk
            label={"Number of Days"}
            {...form.getInputProps("draftValue")}
            disabled={isFormActive}
          />
        )}

        {form.getInputProps("draftMode").value === DraftMode.Dynamic && (
          <Select
            withAsterisk
            label={`Day of ${
              currentFrequency === PaymentFrequency.Monthly
                ? "Month"
                : currentFrequency === PaymentFrequency.Yearly
                ? "Year"
                : "Week"
            }`}
            data={
              (currentFrequency === PaymentFrequency.Yearly
                ? monthArray
                : currentFrequency === PaymentFrequency.Monthly
                ? daysArray
                : weeksArray
              )?.map((val, index) => ({
                value: (1 + index).toString(),
                label: val,
              })) || []
            }
            {...form.getInputProps("draftValue")}
            disabled={isFormActive}
          />
        )}
      </ActionCard>

      <ActionCard
        title={"Pre-sale"}
        mt={"xs"}
        height={HeightCard.sm}
        radius={CardRadius.sm}
        size="md"
        rightIconButton={
          <Switch
            disabled={isFormActive}
            checked={form.getInputProps("isPreSale")?.value}
            {...form.getInputProps("isPreSale")}
            hasBackground={true}
          />
        }
        hideChild={!form.getInputProps("isPreSale")?.value}
        headerOnly
      >
        {form.getInputProps("isPreSale")?.value && (
          <>
            <Text size={13} ml={7} color={theme.colors.secondaryTextColor[6]}>
              Term and first draft day will be postponed until the provided
              date.
            </Text>
            <DatePicker
              dropdownPosition="top-start"
              withAsterisk
              label="Date Input"
              {...form.getInputProps("postponeUntil")}
              disabled={isFormActive}
              mt={"sm"}
            />
          </>
        )}
      </ActionCard>

      <ActionCard
        title={"Group"}
        mt={"sm"}
        height={HeightCard.sm}
        radius={CardRadius.sm}
        size="md"
        rightIconButton={
          <Switch
            disabled={isFormActive}
            checked={form.getInputProps("isGroup")?.value}
            {...form.getInputProps("isGroup")}
            hasBackground={true}
          />
        }
        hideChild={!form.getInputProps("isGroup")?.value}
        headerOnly
      >
        {form.getInputProps("isGroup")?.value && (
          <>
            <Text size={13} ml={7} color={theme.colors.secondaryTextColor[6]}>
              Agreements applied to groups can either have price scaling or a
              member limit. Please select what type of group pricing this
              agreement will support below.
            </Text>
            <Select
              withAsterisk
              data={
                Object.values(GroupPricingType)?.map((val) => ({
                  value: val,
                  label: val,
                })) || []
              }
              disabled={isFormActive}
              label="Pricing Type"
              {...form.getInputProps("groupPricingType")}
            />

            {form.getInputProps("groupPricingType")?.value ===
            GroupPricingType.Limit ? (
              <>
                <NumberInput
                  withAsterisk
                  label="Member Unit"
                  {...form.getInputProps("limit")}
                  disabled={isFormActive}
                />
              </>
            ) : (
              <>
                <NumberInput
                  precision={2}
                  withAsterisk
                  label="Price Per Member"
                  {...form.getInputProps("scalingPrice")}
                  disabled={isFormActive}
                />
              </>
            )}
          </>
        )}
      </ActionCard>

      {isOrg && (
        <ActionCard
          title={"Apply To All Locations"}
          mt={"sm"}
          height={HeightCard.sm}
          radius={CardRadius.sm}
          size="md"
          rightIconButton={
            <Switch
              disabled={isFormActive}
              checked={form.getInputProps("isGeneric")?.value}
              {...form.getInputProps("isGeneric")}
              hasBackground={true}
            />
          }
          hideChild={form.getInputProps("isGeneric")?.value}
          headerOnly
        >
          {!form.getInputProps("isGeneric")?.value && (
            <>
              <Text size={13} ml={7} color={theme.colors.secondaryTextColor[6]}>
                Select the locations to which this agreement should be
                available.
              </Text>
              <MultiSelect
                data={
                  locArr?.locationMany?.map((val) => ({
                    value: val.id,
                    label: val.name,
                  })) || []
                }
                disabled={isFormActive}
                label="Locations"
                {...form.getInputProps("locations")}
                value={form.values.locations}
              />
            </>
          )}
        </ActionCard>
      )}

      {!agreementId && (
        <Group position="apart" mt="md" style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={() => modals.closeAll()}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            radius="md"
            type="submit"
            disabled={createLoading || updateLoading}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            loading={createLoading || updateLoading}
          >
            Create
          </Button>
        </Group>
      )}
    </FormCard>
  );
}

export default memo(AgreementForm);
